
import React from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Notification } from "@/hooks/useNotificationDetail";
import StatusSelect from "./form/StatusSelect";
import { updateNotificationInDb } from "@/services/notificationUpdateService";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { sendDecoprofWebhook } from "@/services/webhookService";

interface NotificationViewFormProps {
  notification: Notification;
}

const NotificationViewForm = ({ notification }: NotificationViewFormProps) => {
  // Format priority for display
  const displayPriority = notification.priority === 'normaal' ? '-' : notification.priority;
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const handleStatusChange = async (newStatus: string) => {
    if (newStatus === notification.status) {
      return; // Status is already set to this value
    }

    const oldStatus = notification.status;
    
    try {
      console.log("Updating notification status", { 
        id: notification.id,
        oldStatus,
        newStatus,
        updatedBy: user?.name
      });
      
      // Update the notification status
      await updateNotificationInDb(
        notification.id,
        notification,
        { status: newStatus },
        user?.id,
        user?.name
      );
      
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['notification', notification.id] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      
      toast.success("Status bijgewerkt");

      // Direct webhook call if status is changed to actie_decoprof
      if (newStatus === 'actie_decoprof') {
        try {
          console.log("Directly triggering Decoprof webhook from NotificationViewForm");
          const updatedNotification = {
            ...notification,
            status: newStatus
          };
          
          const webhookResult = await sendDecoprofWebhook(
            updatedNotification, 
            oldStatus
          );
          
          console.log("Direct webhook call result:", webhookResult);
          
          if (!webhookResult) {
            toast.error("De status is gewijzigd, maar de webhook kon niet worden verzonden.");
          }
        } catch (webhookError) {
          console.error("Error directly sending decoprof webhook:", webhookError);
          toast.error("De status is gewijzigd, maar de webhook kon niet worden verzonden.");
        }
      }
    } catch (error) {
      console.error("Error updating notification status:", error);
      toast.error("Fout bij het bijwerken van de status");
    }
  };

  
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="type">Type</Label>
          <Input 
            id="type" 
            value={notification.type} 
            readOnly 
            className="bg-muted" 
          />
        </div>

        <div>
          <Label htmlFor="vo_number">VO Nummer</Label>
          <Input
            id="vo_number"
            value={notification.vo_number || ""}
            readOnly
            className="bg-muted"
          />
        </div>
      </div>

      <div>
        <Label htmlFor="exact_order_number">Exact Ordernummer</Label>
        <Input
          id="exact_order_number"
          value={notification.exact_order_number || ""}
          readOnly
          className="bg-muted"
        />
      </div>

      <StatusSelect 
        status={notification.status}
        onStatusChange={handleStatusChange}
        label="Status"
        required={false}
      />

      <div>
        <Label htmlFor="message">Bericht</Label>
        <Textarea
          id="message"
          value={notification.message}
          readOnly
          className="bg-muted min-h-[150px]"
          rows={8}
        />
      </div>

      <div>
        <Label htmlFor="priority">Prioriteit</Label>
        <Input
          id="priority"
          value={displayPriority}
          readOnly
          className="bg-muted capitalize"
        />
      </div>
    </div>
  );
};

export default NotificationViewForm;
